import { FC } from 'react';
import { Grid, Select, MenuItem, FormControl, InputLabel, Chip, Box, FormHelperText } from '@mui/material';
import { FieldRow, FieldLabel, Panel, Title, RadioLabel } from 'src/generic/component';
import { BooleanRadio, Checkboxes, Input } from 'src/generic/component/form';
import { convertToFormattedDate, convertToIsoDate } from 'src/utils';
import { Draft } from 'src/types/common';
import { IAIRequest, IExamsInformation } from 'src/types/org/ai/schoolInterface';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { PRE_AP_COURSES_OPTIONS, AP_COURSES_OPTIONS, SAT_ADMINISTRATION_DATES } from '../aiFormConstants';
import { getValidSATDates } from 'src/utils/utils';
import { Dropdown } from 'src/components';

interface IExamsInformationPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const ExamsInformationPanel: FC<IExamsInformationPanelProps> = ({
  item,
  readOnly,
  errors,
  onUpdate
}) => {
  const { getError } = ErrorManager(errors);

  const appForm = item.data?.appForm;
  const {
    interestedInAPCourses,
    confirmAPGradeLevels,
    interestedInPreAPCourses,
    interestedInPSATSuite,
    interestedInSAT,
    satAdministrationTypes,
    earliestAdministrationDate,
    apCoursesOffered,
    preAPCoursesOffered,
  } = appForm?.examsInformation || {};

  // SAT Administration Type Options
  const SAT_ADMINISTRATION_OPTIONS = ['School Day', 'Weekend'];

  const validSATDates = getValidSATDates(SAT_ADMINISTRATION_DATES);

  const changed = (updates: Partial<IExamsInformation>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        appForm: {
          ...appForm,
          examsInformation: {
            ...appForm?.examsInformation,
            ...updates
          }
        }
      }
    };
    onUpdate(updatedItem);
  };

  return (
    <Panel>
      <Title>College Board Programs</Title>
      <Grid container spacing={5}>
        {/* Left Side Grid */}
        <Grid item xs={6}>
          {/* AP Courses Interest */}
          <FieldRow>
            <RadioLabel variant="body1">Is your organization interested in offering AP Courses?</RadioLabel>
            <BooleanRadio
              readOnly={readOnly}
              error={getError('data.appForm.examsInformation.interestedInAPCourses')}
              value={interestedInAPCourses}
              name='interestedInAPCourses'
              onChange={(value) => {
                changed({
                  interestedInAPCourses: value,
                  confirmAPGradeLevels: value ? confirmAPGradeLevels : undefined,
                  apCoursesOffered: value ? apCoursesOffered : undefined  // Clear selected courses when "No" is selected
                });
              }}
            />
          </FieldRow>

          {/* AP Grade Levels Confirmation (conditional) */}
          {interestedInAPCourses && (
            <FieldRow>
              <RadioLabel variant="body1">
                Please confirm your organization will offer AP courses in accordance with the appropriate grade levels policy: {' '}
                <a
                  href="https://apcentral.collegeboard.org/courses/ap-course-audit/faqs/particular-grade-levels-for-ap-courses"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#1976d2',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  Appropriate AP Courses Grade Levels
                </a>
              </RadioLabel>
              <BooleanRadio
                readOnly={readOnly}
                error={getError('data.appForm.examsInformation.confirmAPGradeLevels')}
                value={confirmAPGradeLevels}
                name='confirmAPGradeLevels'
                onChange={(value) => {
                  changed({ confirmAPGradeLevels: value });
                }}
              />
            </FieldRow>
          )}
          {/* AP Courses Selection using Material-UI Multi-Select */}
          {interestedInAPCourses && (
            <FieldRow>
              <FieldLabel variant="body1">Please select which AP Courses your organization is interested in offering</FieldLabel>
              <FormControl
                fullWidth
                variant="outlined"
                style={{ marginTop: '12px' }}
                error={!!getError('data.appForm.examsInformation.apCoursesOffered')}
              >
                <InputLabel>Choose AP courses...</InputLabel>
                <Select
                  multiple
                  value={apCoursesOffered || []}
                  onChange={(event) => {
                    const value = event.target.value as string[];
                    changed({ apCoursesOffered: value });
                  }}
                  disabled={readOnly}
                  label="Choose AP courses..."
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return '';
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            style={{ maxWidth: '200px' }}
                          />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: 250,
                      },
                    },
                  }}
                >
                {AP_COURSES_OPTIONS.map((option) => {
                const isSelected = (apCoursesOffered || []).includes(option.value);
                return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      // selected row: blue bg + bold green text
                      '&.Mui-selected': {
                        backgroundColor: '#BBDEFB !important',
                        color: '#2E7D32 !important',
                        fontWeight: 700,
                      },
                      // hover on selected row: yellow
                      '&.Mui-selected:hover': {
                        backgroundColor: '#FFEB3B !important',
                        color: '#2E7D32 !important',
                      },
                      // hover on unselected row: yellow
                      '&:hover': {
                        backgroundColor: '#FFEB3B !important',
                      },
                      // keep unselected default text
                      color: isSelected ? '#2E7D32' : 'inherit',
                      fontWeight: isSelected ? 700 : 400,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                  );
                })}
                </Select>
                {getError('data.appForm.examsInformation.apCoursesOffered') && (
                  <FormHelperText>{getError('data.appForm.examsInformation.apCoursesOffered')}</FormHelperText>
                )}
              </FormControl>

              {/* Selection counter */}
              {apCoursesOffered && apCoursesOffered.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                  {apCoursesOffered.length} course{apCoursesOffered.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </FieldRow>
          )}

          {/*  Pre-AP Courses Interest */}
          <FieldRow>
            <RadioLabel variant="body1">Is your institution interested in offering Pre-AP Courses?</RadioLabel>
            <BooleanRadio
              readOnly={readOnly}
              error={getError('data.appForm.examsInformation.interestedInPreAPCourses')}
              value={interestedInPreAPCourses}
              name='interestedInPreAPCourses'
              onChange={(value) => {
                changed({
                  interestedInPreAPCourses: value,
                  preAPCoursesOffered: value ? preAPCoursesOffered : undefined
                });
              }}
            />
          </FieldRow>
          {interestedInPreAPCourses && (
            <FieldRow>
              <FieldLabel variant="body1">Please select which Pre-AP courses your organization is interested in offering</FieldLabel>
              <FormControl
                fullWidth
                variant="outlined"
                style={{ marginTop: '12px' }}
                error={!!getError('data.appForm.examsInformation.preAPCoursesOffered')}
              >
                <InputLabel>Choose Pre-AP courses...</InputLabel>
                <Select
                  multiple
                  value={preAPCoursesOffered || []}
                  onChange={(event) => {
                    const value = event.target.value as string[];
                    changed({ preAPCoursesOffered: value });
                  }}
                  disabled={readOnly}
                  label="Choose Pre-AP courses..."
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return '';

                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            style={{ maxWidth: '200px' }}
                          />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: 250,
                      },
                    },
                  }}
                >
                {PRE_AP_COURSES_OPTIONS.map((option) => {
                  const isSelected = (preAPCoursesOffered || []).includes(option.value);

                  return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      // selected row: light blue bg + bold green text
                      '&.Mui-selected': {
                        backgroundColor: '#BBDEFB !important',
                        color: '#2E7D32 !important',
                        fontWeight: 700,
                      },
                      // hover on selected row: yellow
                      '&.Mui-selected:hover': {
                        backgroundColor: '#FFEB3B !important',
                        color: '#2E7D32 !important',
                      },
                      // hover on unselected row: yellow
                      '&:hover': {
                        backgroundColor: '#FFEB3B !important',
                      },
                      // selected text styling
                      color: isSelected ? '#2E7D32' : 'inherit',
                      fontWeight: isSelected ? 700 : 400,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                  );
                })}
                </Select>
                {getError('data.appForm.examsInformation.preAPCoursesOffered') && (
                  <FormHelperText>{getError('data.appForm.examsInformation.preAPCoursesOffered')}</FormHelperText>
                )}
              </FormControl>

              {/* Selection counter */}
              {preAPCoursesOffered && preAPCoursesOffered.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                  {preAPCoursesOffered.length} course{preAPCoursesOffered.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </FieldRow>
          )}
        </Grid>

        {/* Right Side Grid */}
        <Grid item xs={6}>
          {/* PSAT Suite Interest */}
          <FieldRow>
            <RadioLabel variant="body1">Is your institution interested in administering the PSAT suite of assessments?</RadioLabel>
            <BooleanRadio
              readOnly={readOnly}
              error={getError('data.appForm.examsInformation.interestedInPSATSuite')}
              value={interestedInPSATSuite}
              name='interestedInPSATSuite'
              onChange={(value) => {
                changed({ interestedInPSATSuite: value });
              }}
            />
          </FieldRow>

          {/* SAT Interest */}
          <FieldRow>
            <RadioLabel variant="body1">Is your institution interested in administering SAT?</RadioLabel>
            <BooleanRadio
              readOnly={readOnly}
              error={getError('data.appForm.examsInformation.interestedInSAT')}
              value={interestedInSAT}
              name='interestedInSAT'
              onChange={(value) => {
                changed({
                  interestedInSAT: value,
                  satAdministrationTypes: value ? satAdministrationTypes : undefined,
                  earliestAdministrationDate: value ? earliestAdministrationDate : undefined
                });
              }}
            />
          </FieldRow>

        {/* SAT Administration Types (conditional) */}
        {interestedInSAT && (
          <FieldRow>
            <FieldLabel variant="body1">
              Is your institution interested in administering SAT during the school day or as a weekend test center?{' '}
              <a
                href="https://satsuite.collegeboard.org/k12-educators/educator-experience/comparing-sat-weekend-school-day"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#1976d2',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                SAT Weekend vs School Day
              </a>
            </FieldLabel>
            <div className="organization-type-checkboxes" style={{ marginTop: '12px' }}>
              <Checkboxes
                key={`sat-admin-${(satAdministrationTypes || []).join(',')}`}
                name='satAdministrationTypes'
                readOnly={readOnly}
                value={satAdministrationTypes || []}
                error={getError('data.appForm.examsInformation.satAdministrationTypes')}
                options={SAT_ADMINISTRATION_OPTIONS.map(option => ({ key: option, label: option }))}
                onChange={(values: string[]) => {
                  // Check if 'Weekend' was removed from selection
                  const weekendWasSelected = satAdministrationTypes?.includes('Weekend');
                  const weekendIsSelected = values.includes('Weekend');
                  
                  // If Weekend was previously selected but now it's not, clear the date
                  if (weekendWasSelected && !weekendIsSelected) {
                    changed({ 
                      satAdministrationTypes: values,
                      earliestAdministrationDate: undefined  // Clear the date
                    });
                  } else {
                    // Otherwise just update the administration types
                    changed({ satAdministrationTypes: values });
                  }
                }}
              />
            </div>
          </FieldRow>
        )}

          {/* Earliest Administration Date (conditional) - Only show when Weekend is selected */}
          {interestedInSAT && satAdministrationTypes?.includes('Weekend') && (
            <FieldRow>
              {readOnly ? (
                <FieldLabel>Earliest Administration Date: {convertToFormattedDate(earliestAdministrationDate)}</FieldLabel>
              ) : (
                <>
                  <FieldLabel variant="body1">Please select the earliest administration date your institution is interested in below</FieldLabel>
                  <Dropdown
                    values={[
                      { value: '', label: 'Select one' },
                      ...validSATDates
                    ]}
                    value={earliestAdministrationDate || ''}
                    onChange={(value: string) => {
                      changed({ earliestAdministrationDate: value });
                    }}
                    validation={getError('data.appForm.examsInformation.earliestAdministrationDate') ? 'error' : undefined}
                    validationMsg={getError('data.appForm.examsInformation.earliestAdministrationDate')}
                  />
                </>
              )}
            </FieldRow>
          )}
        </Grid>
      </Grid>
    </Panel>
  );
};